import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Boxes, Plus, Trash2, Edit, AlertTriangle, Search } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function RawMaterialsPage() {
  const [materials, setMaterials] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ name: '', sku: '', category: '', quantity: 0, unit: 'kg', cost_per_unit: 0, min_stock: 0, supplier: '', location: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try { const res = await api.get('/factory/raw-materials'); setMaterials(res.data); } catch { toast.error('Failed'); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    if (!form.name) return toast.error('Name required');
    setSaving(true);
    try {
      const payload = { ...form, quantity: parseFloat(form.quantity) || 0, cost_per_unit: parseFloat(form.cost_per_unit) || 0, min_stock: parseFloat(form.min_stock) || 0 };
      if (editing) { await api.put(`/factory/raw-materials/${editing}`, payload); toast.success('Updated'); }
      else { await api.post('/factory/raw-materials', payload); toast.success('Material added'); }
      setShowDialog(false); setEditing(null);
      setForm({ name: '', sku: '', category: '', quantity: 0, unit: 'kg', cost_per_unit: 0, min_stock: 0, supplier: '', location: '' });
      fetchData();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const handleEdit = (m) => {
    setEditing(m.id);
    setForm({ name: m.name || '', sku: m.sku || '', category: m.category || '', quantity: m.quantity || 0, unit: m.unit || 'kg', cost_per_unit: m.cost_per_unit || 0, min_stock: m.min_stock || 0, supplier: m.supplier || '', location: m.location || '' });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete?')) return;
    try { await api.delete(`/factory/raw-materials/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const filtered = materials.filter(m => !search || m.name?.toLowerCase().includes(search.toLowerCase()) || m.sku?.toLowerCase().includes(search.toLowerCase()));
  const lowStock = materials.filter(m => m.quantity <= m.min_stock && m.min_stock > 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="raw-materials-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Boxes size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="raw-materials-title">Raw Materials</h1>
              <p className="text-[13px] text-muted-foreground mt-0.5 font-medium">Inventory of raw materials & supplies</p>
            </div>
          </div>
          <div className="flex gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 pr-3 py-2 text-[13px] rounded-xl border border-border bg-white/80 focus:outline-none focus:ring-2 focus:ring-primary/20 w-44" />
            </div>
            <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button className="rounded-xl h-9 text-[13px]" data-testid="add-material-btn"><Plus size={14} className="mr-1.5" />Add Material</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg" data-testid="material-dialog">
                <DialogHeader><DialogTitle className="font-outfit">{editing ? 'Edit' : 'Add'} Raw Material</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-[12px]">Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="material-name" /></div>
                    <div><Label className="text-[12px]">SKU</Label><Input value={form.sku} onChange={e => setForm({...form, sku: e.target.value})} /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div><Label className="text-[12px]">Category</Label><Input value={form.category} onChange={e => setForm({...form, category: e.target.value})} /></div>
                    <div><Label className="text-[12px]">Quantity</Label><Input type="number" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} /></div>
                    <div><Label className="text-[12px]">Unit</Label>
                      <Select value={form.unit} onValueChange={v => setForm({...form, unit: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{['kg', 'g', 'liters', 'ml', 'pcs', 'meters', 'tons', 'boxes'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-[12px]">Cost/Unit</Label><Input type="number" value={form.cost_per_unit} onChange={e => setForm({...form, cost_per_unit: e.target.value})} /></div>
                    <div><Label className="text-[12px]">Min Stock Level</Label><Input type="number" value={form.min_stock} onChange={e => setForm({...form, min_stock: e.target.value})} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-[12px]">Supplier</Label><Input value={form.supplier} onChange={e => setForm({...form, supplier: e.target.value})} /></div>
                    <div><Label className="text-[12px]">Location</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} /></div>
                  </div>
                  <Button onClick={handleSave} disabled={saving} className="w-full rounded-xl">{saving ? 'Saving...' : editing ? 'Update' : 'Add Material'}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="stat-card border-0 bg-white"><CardContent className="pt-4 pb-3.5 px-4"><p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Total Materials</p><p className="text-2xl font-bold font-outfit text-foreground mt-1">{materials.length}</p></CardContent></Card>
          <Card className="stat-card border-0 bg-white"><CardContent className="pt-4 pb-3.5 px-4"><p className="text-[10px] font-semibold text-error uppercase tracking-[0.08em]">Low Stock</p><p className="text-2xl font-bold font-outfit text-error mt-1">{lowStock.length}</p></CardContent></Card>
          <Card className="stat-card border-0 bg-white"><CardContent className="pt-4 pb-3.5 px-4"><p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Categories</p><p className="text-2xl font-bold font-outfit text-info mt-1">{new Set(materials.map(m => m.category).filter(Boolean)).size}</p></CardContent></Card>
          <Card className="stat-card border-0 bg-white"><CardContent className="pt-4 pb-3.5 px-4"><p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Total Value</p><p className="text-2xl font-bold font-outfit text-success mt-1">{cur()} {materials.reduce((s, m) => s + (m.quantity || 0) * (m.cost_per_unit || 0), 0).toLocaleString()}</p></CardContent></Card>
        </div>

        {/* Low Stock Alert */}
        {lowStock.length > 0 && (
          <Card className="border-0 bg-error/10 shadow-sm" data-testid="low-stock-alert">
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-2 text-[13px]">
                <AlertTriangle size={16} className="text-error" />
                <span className="font-medium text-error">{lowStock.length} material(s) below minimum stock:</span>
                <div className="flex gap-1.5 flex-wrap">
                  {lowStock.map(m => <Badge key={m.id} className="bg-error/20 text-error text-[10px]">{m.name}: {m.quantity}/{m.min_stock} {m.unit}</Badge>)}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white rounded-2xl overflow-hidden">
          <CardContent className="px-0 pt-4">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="raw-materials-table">
                <thead><tr>
                  <th className="text-left">Material</th>
                  <th className="text-left">Category</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Cost/Unit</th>
                  <th className="text-right">Total Value</th>
                  <th className="text-left">Supplier</th>
                  <th className="text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {filtered.map(m => (
                    <tr key={m.id} className={`group ${m.quantity <= m.min_stock && m.min_stock > 0 ? 'bg-error/10' : ''}`} data-testid="material-row">
                      <td className="font-medium text-foreground">
                        {m.name}
                        {m.sku && <span className="text-[11px] text-muted-foreground ml-1.5">{m.sku}</span>}
                        {m.quantity <= m.min_stock && m.min_stock > 0 && <Badge className="ml-1.5 bg-error/20 text-error text-[9px]">Low</Badge>}
                      </td>
                      <td><Badge variant="outline" className="text-[11px]">{m.category || '-'}</Badge></td>
                      <td className="text-right font-semibold tabular-nums">{m.quantity} {m.unit}</td>
                      <td className="text-right tabular-nums">{cur()} {(m.cost_per_unit || 0).toFixed(2)}</td>
                      <td className="text-right font-semibold tabular-nums">{cur()} {((m.quantity || 0) * (m.cost_per_unit || 0)).toFixed(2)}</td>
                      <td className="text-muted-foreground">{m.supplier || '-'}</td>
                      <td className="text-right">
                        <div className="flex gap-1 justify-end opacity-80 group-hover:opacity-100">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(m)}><Edit size={13} /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-error" onClick={() => handleDelete(m.id)}><Trash2 size={13} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-muted-foreground text-[13px]">No materials found</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
